import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Star, Quote, Loader2 } from 'lucide-react';
import { testimonialsAPI } from '../services/api';

export const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load testimonials from API
  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await testimonialsAPI.getAll();
        setTestimonials(data);
      } catch (err) {
        console.error('Error loading testimonials:', err);
        setError('Erreur lors du chargement des témoignages');
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-yellow-100 text-yellow-800 px-4 py-2">
              Témoignages
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ils Nous Font
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-gray-700">
                Confiance
              </span>
            </h2>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-yellow-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Chargement des témoignages...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">⚠️</div>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-yellow-100 text-yellow-800 px-4 py-2">
            Témoignages
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ils Nous Font
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-gray-700">
              Confiance
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Découvrez les retours de nos clients satisfaits en France et en Afrique. 
            Leur confiance est notre plus belle récompense.
          </p>
        </div>

        {/* Testimonials Grid */}
        {testimonials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Aucun témoignage disponible pour le moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-md hover:scale-105 bg-white relative overflow-hidden">
                {/* Quote decoration */}
                <div className="absolute top-4 right-4 opacity-10">
                  <Quote className="w-12 h-12 text-yellow-600" />
                </div>
                
                <CardContent className="p-6">
                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-4">
                    {renderStars(testimonial.rating)}
                  </div>

                  {/* Testimonial text */}
                  <blockquote className="text-gray-700 mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className="bg-yellow-100 text-yellow-600 font-semibold">
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Trust indicators */}
        <div className="bg-gradient-to-r from-yellow-50 to-gray-50 rounded-2xl p-8 md:p-12">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Pourquoi Choisir Z&N Solar ?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-yellow-600">2+</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Années d'Expérience</h4>
                <p className="text-gray-600 text-sm">Expertise reconnue dans le secteur énergétique</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-yellow-600">+30</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Installations réalisées chaque année</h4>
                <p className="text-gray-600 text-sm">Résidentiel, professionnel et commercial</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-yellow-600">24h</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Support Réactif</h4>
                <p className="text-gray-600 text-sm">Réponse garantie sous 24h</p>
              </div>

            {/* Satisfaction badge */}
            <div className="mt-8 flex justify-center">
              <div className="gold-ribbon">
                <span className="icon">★</span>
                Satisfait ou remboursé
                <span className="icon">★</span>
              </div>
            </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};